import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';

export type SaveDestination = 'local' | 'fias';

interface ConfirmCloseDialogProps {
  /** Filename being closed — shown in the dialog title for context. */
  fileName: string;
  isSaving: boolean;
  /** Error message from the most recent save attempt (FSA permission
   *  revoked, Fias upload failed, quota exceeded, etc.). Surfaced so the
   *  user sees something actionable instead of the dialog hanging. */
  saveError?: string | null;
  onCancel: () => void;
  onDiscard: () => void;
  /** Save the tab to wherever it was opened from (Fias if it has a fileId,
   *  else local download). Parent leaves the dialog open while `isSaving`
   *  is true and closes it after the save resolves. */
  onSave: () => void;
  /** Open the full Save-As / Export dialog so the user can change the name,
   *  destination, format, and dimensions. */
  onSaveAs: () => void;
}

/**
 * Confirmation dialog shown when the user closes a tab that has unsaved
 * changes. Offers Discard / Save / Save As. "Save" routes the file back to
 * its original location (Fias or local); destination overrides happen in
 * the Save As dialog rather than here.
 */
export function ConfirmCloseDialog({
  fileName,
  isSaving,
  saveError,
  onCancel,
  onDiscard,
  onSave,
  onSaveAs,
}: ConfirmCloseDialogProps) {
  // All four buttons share the same neutral outline style so no single
  // action is highlighted as the "default" — keyboard / hover discovery
  // applies equally to Cancel / Discard / Save as / Save.
  const BTN_BASE =
    'px-4 py-2 text-sm rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50';

  return (
    <div className={MODAL_OVERLAY}>
      <div
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full max-w-md p-6 space-y-4`}
      >
        <h2 className="text-lg font-semibold">Save changes to &ldquo;{fileName}&rdquo;?</h2>
        <p className="text-sm text-muted-foreground">
          Your changes will be lost if you don&rsquo;t save.
        </p>

        {saveError && (
          <div
            className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
            role="alert"
          >
            {saveError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <button type="button" className={BTN_BASE} onClick={() => onCancel()} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className={BTN_BASE}
            onClick={() => onDiscard()}
            disabled={isSaving}
          >
            Discard
          </button>
          <button type="button" className={BTN_BASE} onClick={() => onSaveAs()} disabled={isSaving}>
            Save as&hellip;
          </button>
          <button type="button" className={BTN_BASE} onClick={() => onSave()} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
